import { BaseDataDefinition } from "./BaseDataDefinition";
import { DataTypes } from "../DataTypes";
import { ValidationIssue } from "../../IDesignValidationProvider";
import { format, parseISO, parseJSON } from "date-fns";
import { JSONDummyData } from "../Constants/DataDefinitionConstants";
import type { AppGlueAny } from "../../AppGlueAny";

export class DateDataDefinition extends BaseDataDefinition<Date> {
    readonly type: DataTypes = DataTypes.DATE;

    lowerBounds?: Date;
    upperBounds?: Date;
    storeTime: boolean = true;

    static isDate(v: AppGlueAny): boolean {
        if (typeof v === "string") {
            let d = new Date(v);
            return !isNaN(d.getTime());
        } else if (v instanceof Date) {
            return !isNaN(v.getTime());
        }
        return false;
    }

    static fromJSONObjectValue(value: AppGlueAny, name: string): DateDataDefinition | undefined {
        if (Array.isArray(value)) {
            if (!value.find((v) => !DateDataDefinition.isDate(v))) {
                let def = new DateDataDefinition(name);
                def.list = true;

                return def;
            }
        } else if (DateDataDefinition.isDate(value)) {
            let def = new DateDataDefinition(name);
            return def;
        }

        return undefined;
    }

    static fromJSONSchema(obj: object, name?: string): DateDataDefinition | undefined {
        let def = new DateDataDefinition(name);

        let type = Reflect.get(obj, "type");
        if (type === "array") {
            def.list = true;
            let items = Reflect.get(obj, "items");

            type = Reflect.get(items, "type");

            if (type === "date") {
                return def;
            }
        } else if (type === "date") {
            return def;
        }

        return undefined;
    }

    getStorageData(): object {
        let object: object = super.getStorageData();

        Reflect.set(object, "lowerBounds", this.lowerBounds);
        Reflect.set(object, "upperBounds", this.upperBounds);
        Reflect.set(object, "storeTime", this.storeTime);
        return object;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        let lowerBoundsVal = Reflect.get(data, "lowerBounds");
        if (lowerBoundsVal) this.lowerBounds = new Date(lowerBoundsVal);
        let upperBoundsVal = Reflect.get(data, "upperBounds");
        if (upperBoundsVal) this.upperBounds = new Date(upperBoundsVal);
        this.storeTime = Reflect.get(data, "storeTime");
    }

    verifyValueType(value: AppGlueAny): ValidationIssue | undefined {
        if (value === undefined) return new ValidationIssue("Undefined Value");

        if (this.list) {
            // check is array
            if (!Array.isArray(value)) return new ValidationIssue(value + " is not a date array");

            // check each value is of right type
            for (let n of value) {
                if (!DateDataDefinition.isDate(n))
                    return new ValidationIssue(value + " has array members that are not dates");
            }
        } else {
            if (Array.isArray(value)) return new ValidationIssue(value + " is a date array");

            if (!DateDataDefinition.isDate(value)) return new ValidationIssue(value + " is not a date");
        }

        return undefined;
    }

    convert(value: AppGlueAny): AppGlueAny {
        if (value === undefined) return undefined;

        if (this.list) {
            if (Array.isArray(value)) {
                let output: any[] = [];

                for (let n of value) {
                    if (n instanceof Date) {
                        output.push(n);
                    } else {
                        let d = this.parseDate(n);
                        if (d) output.push(d);
                        else output.push(n); // put original object in
                    }
                }

                return output;
            }
            return value;
        } else {
            if (value instanceof Date) {
                return value;
            } else {
                let d = this.parseDate(value);
                if (d) return d;
                else return value; // return original value
            }
        }
    }

    parseDate(value: AppGlueAny): Date | undefined {
        if (value === undefined) return undefined;

        if (typeof value === "number") {
            return undefined;
        }

        if (value instanceof Date) {
            return value;
        }

        if (value === null) return undefined;

        // todo: expand date parsing logic
        let d = parseISO(value.toString());

        if (d.getFullYear()) return d;

        d = parseJSON(value.toString());

        if (d.getFullYear()) return d;

        return undefined;
    }

    toJSONObject(value: AppGlueAny): AppGlueAny {
        // need to convert to string
        let issue = this.verifyValueType(value);

        if (issue) {
            value = JSONDummyData.DATE;

            if (this.list) {
                value = [value];
            }
        }

        if (Array.isArray(value)) {
            return value.map((v) => format(v instanceof Date ? v : new Date(v), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"));
        }

        return format(value instanceof Date ? value : new Date(value), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx");
    }
}
